import styled from '@emotion/styled'
import { useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { JSONEditorInput } from '~/components/JSONEditor'
import { JSON_SPACE_COUNT, WithClassName } from '~/types/utils'

const Footer = styled.div`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
`

const Error = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${Colors.Alert};
`

const Actions = styled.div`
  display: inline-flex;
  gap: 20px;
  align-items: center;
`

export type JSONEditorStatus = {
  error?: boolean
  dirty?: boolean
}

type PureJSONEditorProps<T extends { [key: string]: any }> = WithClassName & {
  data: T
  statusUpdate?: (status: JSONEditorStatus) => void
  onValidate: (objectValue: T) => void
}

export const JSONEditor = <T extends { [key: string]: any }>({ className, data, statusUpdate, onValidate }: PureJSONEditorProps<T>) => {
  const jsonValue = JSON.stringify(data, null, JSON_SPACE_COUNT)
  const defaultValues = {
    jsonValue,
  }
  const form = useForm<{ jsonValue: string }>({
    defaultValues,
    mode: 'onChange',
  })
  const update: SubmitHandler<{ jsonValue: string }> = (formValue) => {
    const res = JSON.parse(formValue.jsonValue)
    onValidate({ ...res })
    form.reset(formValue)
    statusUpdate && statusUpdate({ dirty: false, error: false })
  }

  const restore = () => {
    form.reset(defaultValues)
    statusUpdate && statusUpdate({ dirty: false, error: false })
  }

  useEffect(() => {
    statusUpdate && statusUpdate({ dirty: !form.formState.errors.jsonValue && form.formState.isDirty, error: !!form.formState.errors.jsonValue })
  }, [form.formState.errors.jsonValue, form.formState.isDirty])

  return (
    <FormProvider {...form}>
      <form className={className} onSubmit={form.handleSubmit(update)}>
        <JSONEditorInput control={form.control} name={'jsonValue'} syncLintErrorsWithForm={true} />
        <Footer>
          <Error></Error>
          <Actions>
            {form.formState.isDirty && (
              <Button onClick={restore} iconName={'restore'} variant={'tertiary'} trackingLabel={'adm-admin-scopes-detail-pocoverrides-restore'}>
                Restore
              </Button>
            )}
            <Button disabled={!!form.formState.errors.jsonValue || !form.formState.isDirty} type={'submit'} trackingLabel={'adm-admin-scopes-detail-pocoverrides-save'}>
              Validate
            </Button>
          </Actions>
        </Footer>
      </form>
    </FormProvider>
  )
}
